import CloseRounded from '@mui/icons-material/CloseRounded';
import ExpandLessRounded from '@mui/icons-material/ExpandLessRounded';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { tipChrome } from '@/content/tips';
import { type ResolvedPageTip } from '@/utils/pageTip';

import { TipGlyph } from './TipGlyph';

type Props = {
  tip: ResolvedPageTip;
  /** Open the card. Always visitor-initiated from here. */
  onOpen: () => void;
  onDismiss: () => void;
};

/**
 * The phone's collapsed offer: the tip's glyph, its eyebrow and its headline,
 * in a bar that opens into the card.
 *
 * ## What changed, and why it was the biggest problem here
 *
 * This bar used to carry the eyebrow and nothing else — two or three words
 * (`"Before you buy"`) beside a chevron. There was no reason on it for anybody
 * to press it: it named a category, not an offer, and the one thing a
 * collapsed disclosure has to do is say what opening it would get you. It also
 * sat as an outlined paper pill twelve pixels above a solid, full-width
 * `StickyActionBar`, which is close enough to read as part of that bar — so
 * the most likely interpretation was a stray piece of the chrome below it.
 *
 * Now it carries the headline, which is the sentence the tip is actually
 * about, and it is painted as a small card rather than as a pill: the same
 * accent edge, the same radius and the same glyph as the card it opens into.
 * The family resemblance is load-bearing — it is what tells a visitor that the
 * bar and the panel are one object in two states rather than two unrelated
 * pieces of furniture.
 *
 * ## Why the phone gets a collapsed state at all
 *
 * On a pointer device the card is 360px in the corner of a wide window and
 * covers nothing anyone is reading, so there is nothing to protect the visitor
 * from. On a phone the same card is a sheet across a small screen, and
 * arriving unbidden it would cover the sentence being read. So a phone is
 * offered the tip first and shown it second — on a tap, or once it has passed
 * `TIP_PHONE_OPEN_DEPTH` and has plainly stayed. That is the collapsed-sheet
 * pattern maps and music players settled on: out of the way, one tap from all
 * of it, and the content behind it usable throughout.
 *
 * The glyph pings on the `waiting` cadence while it sits here — three passes,
 * six seconds apart, and then it stops asking for good. That cap is the whole
 * reason a repeating animation is allowed on this site: see `nudgePulseSx`.
 */
export function TipPeekPill({ tip, onOpen, onDismiss }: Props) {
  /**
   * The deck's opening slide, and the only one this bar ever shows.
   *
   * A collapsed bar has one line of headline in it, so it cannot preview three
   * messages and should not try — a rotating pill would be movement in the
   * corner of a phone screen that the visitor has not yet agreed to look at,
   * which is a worse version of the thing the collapsed state exists to avoid.
   * Slide 1 is written as the page's hook (see `content/tips.ts`), so it is
   * also the right one to lead with: open the bar and the deck starts there.
   */
  const [lead] = tip.slides;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        pr: 2,
        overflow: 'hidden',
        borderRadius: 4,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        // The accent edge, and the one mark it shares with the open card
        // besides the glyph. A real border rather than a pseudo-element, so
        // nothing painted inside the bar can ever cover it.
        borderLeft: 4,
        borderLeftColor: 'primary.main',
        boxShadow: 8,
      }}
    >
      {/* Everything except the cross is one target. At this size a separate
          chevron button is a 24px hit area beside a 44px one, and the chevron
          is the part a thumb goes for. Its accessible name comes from the text
          inside it — the eyebrow and the headline, in that order — so a screen
          reader hears the offer rather than "show more". */}
      <ButtonBase
        onClick={onOpen}
        sx={{
          flex: 1,
          minWidth: 0,
          gap: 3,
          px: 3,
          py: 3,
          justifyContent: 'flex-start',
          textAlign: 'left',
        }}
      >
        <TipGlyph size={32} pulse="waiting" />
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="overline" sx={{ color: 'primary.main' }}>
            {lead.eyebrow}
          </Typography>
          {/* One line, clipped. The headlines are written to about eight words
              so most of them fit whole; the few that do not are better cut
              than wrapped, because a two-line headline here makes the bar tall
              enough to be the sheet it is deliberately not yet. */}
          <Typography variant="subtitle2" noWrap sx={{ color: 'text.primary', fontWeight: 600 }}>
            {lead.title}
          </Typography>
        </Stack>
        <ExpandLessRounded fontSize="small" sx={{ flexShrink: 0, color: 'text.secondary' }} />
      </ButtonBase>

      <IconButton
        onClick={onDismiss}
        aria-label={tipChrome.dismiss}
        sx={{ flexShrink: 0, color: 'text.secondary' }}
      >
        <CloseRounded fontSize="small" />
      </IconButton>
    </Box>
  );
}
